import { supabase } from "@/integrations/supabase/client";
import { PREFIXED_LANGS } from "@/i18n/locales";

/**
 * First path segments that belong to the real app and must never be treated as
 * a marketing short-link slug. Anything else that is a single, file-less path
 * segment (e.g. `/wa-1`) is looked up against the `redirects` table.
 */
const RESERVED_SEGMENTS = new Set<string>([
  ...PREFIXED_LANGS, // de, el
  "imprint",
  "impressum",
  "privacy-policy",
  "datenschutz",
  "contact",
  "blueprint",
  "integrations",
  "blog",
  "pages",
  "unsubscribe",
  "admin",
  "auth",
]);

/**
 * Extract a redirect code from the current URL.
 *
 * Supports two shapes:
 *  - Query style (legacy):   `https://devcore.systems/?c=wa-1`
 *  - Clean path (Shopify):   `https://devcore.systems/wa-1`
 *
 * For the path style we only consider a single, file-less segment that is not a
 * reserved app route, so genuine pages still render normally.
 */
function extractCode(): string | null {
  // Query parameter takes priority for backwards compatibility.
  try {
    const q = new URLSearchParams(window.location.search).get("c");
    if (q) return q.trim();
  } catch {
    /* ignore */
  }

  let path = "";
  try {
    path = window.location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  } catch {
    return null;
  }
  if (!path) return null;
  // Must be a single segment without a file extension.
  if (path.includes("/") || path.includes(".")) return null;
  if (RESERVED_SEGMENTS.has(path.toLowerCase())) return null;
  return path;
}

/**
 * Marketing short-link resolver.
 *
 * Links shaped like `https://devcore.systems/wa-1` (or the legacy `/?c=wa-1`)
 * are intercepted before the React app boots. We look the code up in the
 * `redirects` table via the `resolve_redirect` RPC (which also increments the
 * click counter server-side so the count cannot be tampered with from the
 * client) and then hard-redirect the browser to the full UTM target URL.
 *
 * Returns `true` when a redirect was performed (the caller should NOT render the
 * app), `false` when there was no code or the code could not be resolved.
 */
export async function maybeHandleRedirect(): Promise<boolean> {
  const code = extractCode();
  if (!code) return false;

  // Cover whatever is already painted (prerendered HTML) with a branded overlay
  // so the visitor never sees a flash of the homepage during the hop.
  const overlay = showOverlay();

  try {
    const { data, error } = await supabase.rpc("resolve_redirect", {
      _slug: code,
    });
    const row = Array.isArray(data) ? data[0] : data;
    if (!error && row?.target_url && isSafeUrl(row.target_url)) {
      window.location.replace(row.target_url);
      return true;
    }
  } catch {
    /* fall through to rendering the app */
  }

  // Unknown / disabled code: drop the overlay and let the normal app render.
  overlay?.remove();
  return false;
}

/** Only allow http(s) targets to avoid javascript:/data: style abuse. */
function isSafeUrl(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function showOverlay(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const el = document.createElement("div");
  el.id = "redirect-overlay";
  el.setAttribute("role", "status");
  el.setAttribute("aria-label", "Weiterleitung");
  el.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483647",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "background:#070d10",
  ].join(";");
  el.innerHTML = `
    <div style="width:34px;height:34px;border:3px solid rgba(0,179,196,0.25);border-top-color:#00b3c4;border-radius:50%;animation:dc-redirect-spin 0.7s linear infinite"></div>
    <style>@keyframes dc-redirect-spin{to{transform:rotate(360deg)}}</style>
  `;
  document.body.appendChild(el);
  return el;
}
